import React from 'react';
import { Document, Font, Image, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import moment from 'moment';
import Swal from 'sweetalert2';
import Tw_Cen_MT_Negrita from '../../../componentes/font/Tw_Cen_MT/TCB_____.TTF';
import Tw_Cen_MT from '../../../componentes/font/Tw_Cen_MT/TCM_____.TTF';
import { NegocioConfig } from "../../../configuracion/Empresa";
import { formatoMoneda } from "../../../configuracion/validaciones";

Font.register({ family: 'Tw_Cen_MT', src: Tw_Cen_MT });
Font.register({ family: 'Tw_Cen_MT_Negrita', src: Tw_Cen_MT_Negrita });

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Tw_Cen_MT',
        fontSize: 9,
        paddingTop: 35,
        paddingBottom: 45,
        paddingHorizontal: 40,
        backgroundColor: '#ffffff',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#10b981',
        paddingBottom: 8,
    },
    companyInfoLeft: { flexGrow: 1, textAlign: 'left' },
    companyName: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 16, color: '#065f46', marginBottom: 2 },
    companyAddress: { fontSize: 8, color: '#555', marginBottom: 1 },
    logoContainer: { width: 70, height: 70, marginLeft: 15 },
    logo: { width: 70, height: 70, objectFit: 'contain' },
    titleContainer: {
        marginTop: 5,
        marginBottom: 10,
        backgroundColor: '#ecfdf5',
        paddingVertical: 6,
        paddingHorizontal: 10,
        borderRadius: 4,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    reportTitle: {
        fontSize: 12,
        fontFamily: 'Tw_Cen_MT_Negrita',
        color: '#065f46',
        textTransform: 'uppercase',
    },
    reportSubtitle: {
        fontSize: 8,
        color: '#047857',
    },
    table: { width: '100%', marginTop: 5 },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#065f46',
        paddingVertical: 5,
        paddingHorizontal: 4,
        borderRadius: 2,
    },
    colHeader: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 8,
        color: '#ffffff',
        textAlign: 'left',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#e2e8f0',
        paddingVertical: 4,
        paddingHorizontal: 4,
        alignItems: 'center',
    },
    tableRowEven: {
        backgroundColor: '#f8fafc',
    },
    colText: {
        fontSize: 8,
        color: '#334155',
    },
    colCode: { width: '15%' },
    colDesc: { width: '33%' },
    colLote: { width: '16%' },
    colStock: { width: '10%', textAlign: 'center' },
    colCost: { width: '12%', textAlign: 'right' },
    colTotal: { width: '14%', textAlign: 'right' },

    summaryBox: {
        marginTop: 12,
        padding: 8,
        backgroundColor: '#ecfdf5',
        borderRadius: 4,
        borderWidth: 0.5,
        borderColor: '#a7f3d0',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    summaryItem: {
        fontSize: 8,
        color: '#065f46',
    },
    summaryValue: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        color: '#047857',
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 40,
        right: 40,
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderTopWidth: 0.5,
        borderTopColor: '#cbd5e1',
        paddingTop: 5,
        fontSize: 7,
        color: '#94a3b8',
    },
});

export const GenerarReporteGeneralInventarioPDF = async (productos = []) => {
    if (!productos || productos.length === 0) {
        Swal.fire({
            icon: 'warning',
            title: 'Sin datos',
            text: 'No hay productos en inventario para generar el reporte.'
        });
        return;
    }

    try {
        Swal.fire({
            title: 'Generando Reporte de Inventario...',
            text: 'Por favor, espere mientras se calcula y compila el inventario.',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        const blob = await pdf(<ReporteGeneralInventario productos={productos} />).toBlob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        Swal.close();
    } catch (error) {
        Swal.close();
        console.error("Error al generar PDF de inventario:", error);
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo generar el reporte PDF del inventario.'
        });
    }
};

const ReporteGeneralInventario = ({ productos = [] }) => {
    // Aplanar productos con sus lotes o existencia directa
    const filasInventario = [];
    let totalExistenciaGlobal = 0;
    let totalValorizadoGlobal = 0;

    productos.forEach(prod => {
        const costo = parseFloat(prod.costo || prod.price || prod.precio || 0);

        if (prod.lotes && prod.lotes.length > 0) {
            prod.lotes.forEach(lote => {
                const stockLote = Number(lote.existencia || 0);
                const subtotalLote = stockLote * costo;
                totalExistenciaGlobal += stockLote;
                totalValorizadoGlobal += subtotalLote;

                filasInventario.push({
                    id: `${prod.id}-${lote.id}`,
                    codigo: prod.codigo || '—',
                    descripcion: prod.nombre || prod.descripcion || 'Sin nombre',
                    lote: lote.nombre_lote || lote.numeroLote || 'Lote',
                    existencia: stockLote,
                    costo: costo,
                    valorTotal: subtotalLote
                });
            });
        } else {
            const stock = Number(prod.existencia !== undefined ? prod.existencia : (prod.stock || 0));
            const subtotal = stock * costo;
            totalExistenciaGlobal += stock;
            totalValorizadoGlobal += subtotal;

            filasInventario.push({
                id: prod.id,
                codigo: prod.codigo || '—',
                descripcion: prod.nombre || prod.descripcion || 'Sin nombre',
                lote: 'General / Único',
                existencia: stock,
                costo: costo,
                valorTotal: subtotal
            });
        }
    });

    return (
        <Document>
            <Page size="LETTER" style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.companyInfoLeft}>
                        <Text style={styles.companyName}>{NegocioConfig.nombre}</Text>
                        <Text style={styles.companyAddress}>RTN: {NegocioConfig.rtn}</Text>
                        <Text style={styles.companyAddress}>{NegocioConfig.direccion}</Text>
                        <Text style={styles.companyAddress}>Tel: {NegocioConfig.telefono}</Text>
                    </View>
                    {NegocioConfig.logo && (
                        <View style={styles.logoContainer}>
                            <Image src={NegocioConfig.logo} style={styles.logo} />
                        </View>
                    )}
                </View>

                <View style={styles.titleContainer}>
                    <Text style={styles.reportTitle}>Reporte General de Inventario y Existencias</Text>
                    <Text style={styles.reportSubtitle}>Fecha: {moment().format('DD/MM/YYYY HH:mm')}</Text>
                </View>

                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.colHeader, styles.colCode]}>Código</Text>
                        <Text style={[styles.colHeader, styles.colDesc]}>Producto</Text>
                        <Text style={[styles.colHeader, styles.colLote]}>Lote / Ubicación</Text>
                        <Text style={[styles.colHeader, styles.colStock]}>Existencia</Text>
                        <Text style={[styles.colHeader, styles.colCost]}>Costo / P.U.</Text>
                        <Text style={[styles.colHeader, styles.colTotal]}>Valor Total</Text>
                    </View>

                    {filasInventario.map((fila, index) => {
                        const isEven = index % 2 === 0;
                        return (
                            <View key={fila.id || index} style={[styles.tableRow, isEven ? styles.tableRowEven : {}]}>
                                <Text style={[styles.colText, styles.colCode]}>{fila.codigo}</Text>
                                <Text style={[styles.colText, styles.colDesc]}>{fila.descripcion}</Text>
                                <Text style={[styles.colText, styles.colLote]}>{fila.lote}</Text>
                                <Text style={[styles.colText, styles.colStock]}>{fila.existencia}</Text>
                                <Text style={[styles.colText, styles.colCost]}>{formatoMoneda(fila.costo)}</Text>
                                <Text style={[styles.colText, styles.colTotal]}>{formatoMoneda(fila.valorTotal)}</Text>
                            </View>
                        );
                    })}
                </View>

                <View style={styles.summaryBox}>
                    <Text style={styles.summaryItem}>
                        Total de Renglones: <Text style={styles.summaryValue}>{filasInventario.length}</Text>
                    </Text>
                    <Text style={styles.summaryItem}>
                        Existencia Total: <Text style={styles.summaryValue}>{totalExistenciaGlobal}</Text>
                    </Text>
                    <Text style={styles.summaryItem}>
                        Valor Total Inventario: <Text style={styles.summaryValue}>{formatoMoneda(totalValorizadoGlobal)}</Text>
                    </Text>
                </View>

                <View style={styles.footer} fixed>
                    <Text>{NegocioConfig.nombre} — Control de Inventario</Text>
                    <Text render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
                </View>
            </Page>
        </Document>
    );
};

export default ReporteGeneralInventario;
